"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";

import { auth } from "@/auth";
import { prisma } from "@/lib/prisma";
import { deleteLocalProductImages } from "@/lib/upload-files";

type ActionState = {
  ok: boolean;
  message: string;
};

const DEFAULT_ERROR_MESSAGE = "Une erreur est survenue. Réessaie dans un instant.";

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

async function requireAdmin() {
  const session = await auth();

  if (!session?.user) {
    redirect("/connexion?callbackUrl=/admin/articles");
  }

  if (session.user.role !== "ADMIN") {
    redirect("/compte");
  }

  return session;
}

function getString(formData: FormData, key: string) {
  const value = formData.get(key);
  return typeof value === "string" ? value.trim() : "";
}

function getOptionalString(formData: FormData, key: string) {
  const value = getString(formData, key);
  return value.length > 0 ? value : null;
}

function getBoolean(formData: FormData, key: string) {
  const value = formData.get(key);
  return value === "on" || value === "true";
}

function getInt(formData: FormData, key: string) {
  const raw = getString(formData, key);
  if (!raw) return null;

  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function getPrice(formData: FormData, key: string) {
  const raw = getString(formData, key).replace(",", ".");
  if (!raw) return null;

  const parsed = Number.parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function getImageUrls(formData: FormData) {
  const raw = getString(formData, "images");

  if (!raw) {
    return [] as string[];
  }

  return raw
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

async function validateProductForm(formData: FormData, productId?: string) {
  const name = getString(formData, "name");
  const manualSlug = getString(formData, "slug");
  const slug = slugify(manualSlug || name);
  const sku = getOptionalString(formData, "sku");
  const shortDescription = getOptionalString(formData, "shortDescription");
  const description = getOptionalString(formData, "description");
  const price = getPrice(formData, "price");
  const compareAtPrice = getPrice(formData, "compareAtPrice");
  const stock = getInt(formData, "stock");
  const categoryId = getString(formData, "categoryId");
  const isPublished = getBoolean(formData, "isPublished");
  const isFeatured = getBoolean(formData, "isFeatured");
  const isGlitter = getBoolean(formData, "isGlitter");
  const material = getOptionalString(formData, "material");
  const meterage = getOptionalString(formData, "meterage");
  const color = getOptionalString(formData, "color");
  const careInstructions = getOptionalString(formData, "careInstructions");
  const seoTitle = getOptionalString(formData, "seoTitle");
  const seoDescription = getOptionalString(formData, "seoDescription");
  const imageUrls = getImageUrls(formData);

  if (!name) {
    return { error: "Le nom de l'article est requis." };
  }

  if (!slug) {
    return { error: "Le slug est invalide." };
  }

  if (price === null || price < 0) {
    return { error: "Le prix est invalide." };
  }

  if (compareAtPrice !== null && compareAtPrice < 0) {
    return { error: "Le prix barré est invalide." };
  }

  if (stock === null || stock < 0) {
    return { error: "Le stock est invalide." };
  }

  if (!categoryId) {
    return { error: "La catégorie est requise." };
  }

  const category = await prisma.category.findUnique({
    where: { id: categoryId },
    select: { id: true },
  });

  if (!category) {
    return { error: "La catégorie sélectionnée est introuvable." };
  }

  const existingProduct = await prisma.product.findUnique({
    where: { slug },
    select: { id: true },
  });

  if (existingProduct && existingProduct.id !== productId) {
    return { error: "Un article utilise déjà ce slug." };
  }

  return {
    data: {
      name,
      slug,
      sku,
      shortDescription,
      description,
      price,
      compareAtPrice,
      stock,
      categoryId,
      isPublished,
      isFeatured,
      isGlitter,
      material,
      meterage,
      color,
      careInstructions,
      seoTitle,
      seoDescription,
      imageUrls,
    },
  };
}

export async function createProduct(
  _prevState: ActionState,
  formData: FormData,
): Promise<ActionState> {
  await requireAdmin();

  try {
    const validated = await validateProductForm(formData);

    if ("error" in validated) {
      return {
        ok: false,
        message: validated.error ?? "Erreur de validation.",
      };
    }

    const { imageUrls, ...productData } = validated.data;

    await prisma.product.create({
      data: {
        ...productData,
        images: imageUrls.length
          ? {
            create: imageUrls.map((url, index) => ({
                imageUrl: url,
                alt: productData.name,
                sortOrder: index,
              })),
            }
          : undefined,
      },
    });

    revalidatePath("/admin/articles");
    revalidatePath("/boutique");

    return {
      ok: true,
      message: "Article créé avec succès.",
    };
  } catch (error) {
    console.error("createProduct", error);

    return {
      ok: false,
      message: DEFAULT_ERROR_MESSAGE,
    };
  }
}

export async function updateProduct(
  _prevState: ActionState,
  formData: FormData,
): Promise<ActionState> {
  await requireAdmin();

  const productId = getString(formData, "productId");

  if (!productId) {
    return {
      ok: false,
      message: "Produit introuvable.",
    };
  }

  try {
    const existingProduct = await prisma.product.findUnique({
      where: { id: productId },
      select: {
        id: true,
        slug: true,
        images: {
          select: {
            imageUrl: true,
          },
        },
      },
    });

    if (!existingProduct) {
      return {
        ok: false,
        message: "Produit introuvable.",
      };
    }

    const validated = await validateProductForm(formData, productId);

    if ("error" in validated) {
      return {
        ok: false,
        message: validated.error ?? "Erreur de validation.",
      };
    }

    const { imageUrls, ...productData } = validated.data;
    const previousImageUrls = existingProduct.images.map((image) => image.imageUrl);
    const removedImageUrls = previousImageUrls.filter(
      (imageUrl) => !imageUrls.includes(imageUrl),
    );

    await prisma.product.update({
      where: { id: productId },
      data: {
        ...productData,
        images: {
          deleteMany: {},
          create: imageUrls.map((url, index) => ({
            imageUrl: url,
            alt: productData.name,
            sortOrder: index,
          })),
        },
      },
    });
    await deleteLocalProductImages(removedImageUrls);

    revalidatePath("/admin/articles");
    revalidatePath(`/admin/articles/${productId}/edit`);
    revalidatePath("/boutique");
    revalidatePath(`/produit/${existingProduct.slug}`);
    revalidatePath(`/produit/${productData.slug}`);

    return {
      ok: true,
      message: "Article mis à jour avec succès.",
    };
  } catch (error) {
    console.error("updateProduct", error);

    return {
      ok: false,
      message: DEFAULT_ERROR_MESSAGE,
    };
  }
}

export async function deleteProduct(
  _prevState: ActionState,
  formData: FormData,
): Promise<ActionState> {
  await requireAdmin();

  const productId = getString(formData, "productId");

  if (!productId) {
    return {
      ok: false,
      message: "Produit introuvable.",
    };
  }

  try {
    const existingProduct = await prisma.product.findUnique({
      where: { id: productId },
      select: {
        id: true,
        slug: true,
        images: {
          select: {
            imageUrl: true,
          },
        },
      },
    });

    if (!existingProduct) {
      return {
        ok: false,
        message: "Produit introuvable.",
      };
    }

    const productImageUrls = existingProduct.images.map((image) => image.imageUrl);
    await prisma.product.delete({
      where: { id: productId },
    });
    await deleteLocalProductImages(productImageUrls);

    revalidatePath("/admin/articles");
    revalidatePath("/boutique");
    revalidatePath(`/produit/${existingProduct.slug}`);

    return {
      ok: true,
      message: "Article supprimé avec succès.",
    };
  } catch (error) {
    console.error("deleteProduct", error);

    return {
      ok: false,
      message: DEFAULT_ERROR_MESSAGE,
    };
  }
}